/**
 * Cliente de Supabase.
 *
 * Es el unico punto del frontend que conoce las credenciales del proyecto, y
 * son credenciales PUBLICAS a proposito: la llave publicable no autoriza nada
 * por si misma. Lo que decide que puede ver y escribir cada quien es el JWT de
 * la sesion mas las politicas RLS y las funciones RPC del servidor.
 *
 * Dicho de otro modo: si alguien copia esta llave del bundle no obtiene nada.
 * Sin iniciar sesion, `anon` no puede leer una sola fila ni ejecutar una sola
 * RPC de escritura.
 *
 * La llave de SERVICIO jamas aparece aqui. Vive solo en la Edge Function que da
 * de alta personal, que es la unica operacion que no se puede hacer desde el
 * navegador.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const publishableKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !publishableKey) {
  // Fallar ruidosamente al arrancar es mejor que una pantalla en blanco con un
  // error de red incomprensible en cada consulta.
  throw new Error(
    'Faltan VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY. Copia .env.example a .env.',
  );
}

export const supabase: SupabaseClient = createClient(url, publishableKey, {
  auth: {
    // La sesion se guarda y se renueva sola. El token de refresco vive en
    // localStorage porque no hay servidor propio que pueda poner una cookie
    // httpOnly: en una arquitectura sin servidor es el mecanismo estandar.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'abarrotes.auth',
  },
  db: { schema: 'public' },
  global: {
    headers: { 'x-application-name': 'abarrotes-pwa' },
  },
  realtime: {
    // Suficiente para el ritmo de una tienda; evita saturar el canal si una
    // sucursal grande dispara muchos eventos seguidos.
    params: { eventsPerSecond: 10 },
  },
});

/** URL base de las Edge Functions (alta de personal). */
export const functionsUrl = `${url}/functions/v1`;
